import difference from 'lodash/difference'
import findLastIndex from 'lodash/findLastIndex'
import omit from 'lodash/omit'
import { createReducer } from 'redux-create-reducer'
import type { Playlist, PlaylistId } from 'types/playlist'
import {
  DATA_LOAD,
  GROUP_DELETE_SUCCESS,
  PLAYLIST_ACTION_STATUS_SET,
  PLAYLIST_DELETE_BATCH_SUCCESS,
  PLAYLIST_LOAD_BATCH,
  PLAYLIST_LOAD_PARAMS,
  PLAYLIST_LOAD_PLAIN_UPDATE,
  PLAYLIST_LOAD_STATS_SUCCESS,
  PLAYLIST_LOAD_SUCCESS,
  PLAYLIST_LOAD_ZIP_URLS_SUCCESS,
  PLAYLIST_SHARE_SUCCESS,
  PLAYLIST_SHARES_LOADING,
  PLAYLIST_SHARES_LOADING_ERROR,
  PLAYLIST_SHARES_LOADING_SUCCESS,
  PLAYLIST_UPDATE_SUCCESS,
  PLAYLIST_VERSION_REPLACE_SUCCESS,
  PLAYLISTS_UPDATE,
  PLAYLISTS_UPDATE_SUCCESS,
  PLAYLISTS_UPDATE_FAIL,
  TRACK_DELETE_BATCH_SUCCESS,
  TRACK_LOAD_BATCH,
  TRACK_MERGE_SUCCESS,
} from '@disco/constants/ActionTypes'
import { PLAYLIST } from '@disco/constants/ItemTypes'
import { removeGroupFromEntities, applyChangeSet } from '@disco/utils/immutable'
export const initialState: Record<PlaylistId, Playlist> = {}

function updateShares(state, id, updater) {
  return {
    ...state,
    ...{
      [id]: { ...state[id], shares: updater(state[id].shares) },
    },
  }
}

function processUpdate(state, pendingUpdate, resetTrackInfo = true) {
  const update = {}
  pendingUpdate.forEach(newPlaylist => {
    const id = newPlaylist.playlist_id
    let normalizedPlaylist

    if (resetTrackInfo) {
      const existingPlaylist = state[id]

      if (existingPlaylist && existingPlaylist.id !== newPlaylist.id) {
        // if playlist version changed, we need to update everything
        normalizedPlaylist = {
          ...newPlaylist,
          tracks: newPlaylist.tracks
            ? newPlaylist.tracks.map(track => track.id)
            : [],
          tags: newPlaylist.tags || [],
        }
      } else if (
        existingPlaylist &&
        existingPlaylist.tracks_info &&
        newPlaylist.tracks_info &&
        existingPlaylist.tracks_info.page < newPlaylist.tracks_info.page
      ) {
        // if it's new page of the playlist handle this by adding tracks
        normalizedPlaylist = {
          ...newPlaylist,
          tracks: newPlaylist.tracks
            ? [
                ...existingPlaylist.tracks,
                ...newPlaylist.tracks.map(track => track.id),
              ]
            : existingPlaylist.tracks,
        }
      } else {
        if (
          existingPlaylist &&
          existingPlaylist.timestamp === newPlaylist.timestamp &&
          newPlaylist.tracks &&
          existingPlaylist.tracks && // this check is designed to keep tracks intact if not all the pages in the new playlist loaded
          existingPlaylist.tracks.length >= newPlaylist.tracks.length && // but tracks loaded in the new playlist should 100% match with the existing playlist,
          // otherwise while timestamp didn't change in playlist, but contents of the playlist changed,
          // in most cases it's due track removal or appending, so we shouldn't prevent playlist from updating
          difference(
            newPlaylist.tracks.map(t => t?.id || t),
            existingPlaylist.tracks.slice(0, newPlaylist.tracks.length),
          ).length === 0
        ) {
          // if playlist didn't change, use existing playlist data to keep track pages intact
          normalizedPlaylist = {
            ...existingPlaylist,
            name: newPlaylist.name,
            theme: newPlaylist.theme,
            general_message: newPlaylist.general_message,
            general_message_html: newPlaylist.general_message_html,
            sort_key: newPlaylist.sort_key,
            sort_direction: newPlaylist.sort_direction,
            sections: newPlaylist.sections,
            share: newPlaylist.share,
            user_aliases: newPlaylist.user_aliases,
            track_aliases: newPlaylist.track_aliases,
            playlist_aliases: newPlaylist.playlist_aliases,
            default_zip_mode: newPlaylist.default_zip_mode,
            share_mode: newPlaylist.share_mode,
            watermark: newPlaylist.watermark,
            watermarking_status: newPlaylist.watermarking_status,
            showcase: newPlaylist.showcase,
            album_image_url: newPlaylist.album_image_url,
            color: newPlaylist.color,
            origin: newPlaylist.origin,
          }
        } else if (
          existingPlaylist &&
          existingPlaylist.tracks &&
          !newPlaylist.tracks_info
        ) {
          // if updated playlist doesn't have tracks_info, this means it's
          // not fully serialized playlist, keep existing track data
          normalizedPlaylist = { ...existingPlaylist, ...newPlaylist }
        } else {
          normalizedPlaylist = {
            ...newPlaylist,
            tracks: newPlaylist.tracks
              ? newPlaylist.tracks.map(track => track.id)
              : [],
            sections: newPlaylist.sections,
            tags: newPlaylist.tags || [],
            user_aliases: newPlaylist.user_aliases,
            track_aliases: newPlaylist.track_aliases,
            playlist_aliases: newPlaylist.playlist_aliases,
            origin: newPlaylist.origin,
          }
        }
      }
    } else {
      normalizedPlaylist = newPlaylist
    }

    // if we received update for playlist, reset action status
    normalizedPlaylist = {
      theme: null,
      ...normalizedPlaylist,
      actionStatus: null,
    }
    update[id] = { ...(state[id] || {}), ...normalizedPlaylist }
  })
  return { ...state, ...update }
}

function adjustSectionsOnRemovedTracks(playlist, trackIds) {
  if (
    !playlist.sections ||
    playlist.sections.length === 0 ||
    !playlist.tracks.some(trackId => trackIds.indexOf(trackId) > -1)
  ) {
    return playlist.sections
  }

  let adjustedSections = playlist.sections.slice()

  for (let i = playlist.tracks.length - 1; i >= 0; i--) {
    const trackId = playlist.tracks[i]
    if (trackIds.indexOf(trackId) === -1) continue
    // adjust section positions after removed track
    adjustedSections = adjustedSections.map(s => {
      if (s.position > i) return { ...s, position: s.position - 1 }
      return s
    })
  }

  return adjustedSections
}

export type State = typeof initialState
export const playlistsReducer = createReducer<State, any>(initialState, {
  [DATA_LOAD](state, { data }) {
    if (!data.playlists || !data.playlists.length) return state
    return processUpdate(state, data.playlists)
  },

  [PLAYLIST_LOAD_BATCH](state, action) {
    // exit early if we don't have anything to do
    if (!action.list || action.list.length === 0) return state
    // ensure that params consists of playlists only
    const pendingUpdate = action.list.filter(item => item.type === PLAYLIST)
    return processUpdate(state, pendingUpdate)
  },

  [PLAYLIST_LOAD_PLAIN_UPDATE](state, { playlists }) {
    const update = {}
    playlists.forEach(p => (update[p.playlist_id] = p))
    return { ...state, ...update }
  },

  [PLAYLIST_LOAD_STATS_SUCCESS](state, action) {
    const { playlist } = action
    const {
      id,
      name,
      stats_all,
      stats_by_user,
      playlist_id: updatingKey,
    } = playlist
    if (!state[updatingKey]) return state
    const update = {
      [updatingKey]: {
        ...state[updatingKey]!,
        has_stats_loaded: true,
        id,
        name,
        stats_all,
        stats_by_user,
      },
    }
    return { ...state, ...update }
  },

  [PLAYLIST_VERSION_REPLACE_SUCCESS](state, { replacedPlaylists }) {
    const update = {}
    replacedPlaylists.forEach(p => {
      const { playlist_id, replacement_playlistversion_id } = p

      // we need to update the state only if playlist already exists in it
      if (state[playlist_id]) {
        update[playlist_id] = {
          ...state[playlist_id],
          id: replacement_playlistversion_id,
          url: state[playlist_id].url.replace(
            /\/\d+\/$/,
            `/${replacement_playlistversion_id}/`,
          ),
        }
      }
    })
    return { ...state, ...update }
  },

  [GROUP_DELETE_SUCCESS](state, { group }) {
    return removeGroupFromEntities(state, group)
  },

  [TRACK_LOAD_BATCH](state, action) {
    // exit early if we don't have anything to do
    if (!action.list || action.list.length === 0) return state
    const pendingUpdate: any[] = []
    action.list.forEach(track => {
      if (track.playlist_versions)
        pendingUpdate.push(...track.playlist_versions)
    })
    // while track contain info about playlist_versions, it doesn't provide
    // update about tracks in it, so prevent overriding tracks with null
    return processUpdate(state, pendingUpdate, false)
  },

  [PLAYLIST_DELETE_BATCH_SUCCESS](state, { playlistIds }) {
    return omit(state, playlistIds)
  },

  [TRACK_DELETE_BATCH_SUCCESS](state, { trackIds }) {
    // TODO: we might be able to optimize track deletion by going through
    // playlists prop when we will be sure that it works reliably
    const update = {}
    Object.keys(state).forEach(prop => {
      const playlistId = Number(prop)
      // playlistId is from Object.keys(state), so state[playlistId] is defined
      const p = state[playlistId]!

      if (p.tracks && p.tracks.some(id => trackIds.indexOf(id) > -1)) {
        const tracks = p.tracks.filter(id => {
          return trackIds.indexOf(id) === -1
        })
        const sections = adjustSectionsOnRemovedTracks(p, trackIds)
        update[playlistId] = { ...p, tracks: tracks, sections }
      }
    })
    return { ...state, ...update }
  },

  [TRACK_MERGE_SUCCESS](state, { masterTrack, tracksToMerge }) {
    const trackIds = tracksToMerge.map(t => t.id)
    const update = {}
    Object.keys(state).forEach(prop => {
      const playlistId = Number(prop)
      // playlistId is from Object.keys(state), so state[playlistId] is defined
      const p = state[playlistId]!

      if (p.tracks && p.tracks.some(id => trackIds.indexOf(id) > -1)) {
        const playlistCotainsMasterTrack = p.tracks.indexOf(masterTrack.id) > -1
        const lastItemToReplace = findLastIndex(p.tracks, id => {
          return trackIds.indexOf(id) > -1
        })
        if (lastItemToReplace === -1) return p
        const tracks = p.tracks
          .filter((id, index) => {
            return (
              trackIds.indexOf(id) === -1 ||
              (index === lastItemToReplace && !playlistCotainsMasterTrack)
            )
          })
          .map(id => {
            return trackIds.indexOf(id) === -1 ? id : masterTrack.id
          })
        const trackIdsToRemove =
          lastItemToReplace > -1 && !playlistCotainsMasterTrack
            ? trackIds.filter(
                // lastItemToReplace was verified > -1 above
                id => id !== p.tracks![lastItemToReplace],
              )
            : trackIds
        const sections = adjustSectionsOnRemovedTracks(p, trackIdsToRemove)
        update[playlistId] = { ...p, tracks: tracks, sections }
      }
    })
    return { ...state, ...update }
  },

  [PLAYLIST_SHARE_SUCCESS](state, { playlist, result, duplicate }) {
    const playlistId = playlist.playlist_id
    const resultId = result.id
    if (duplicate) return state
    return updateShares(state, playlistId, shares => ({
      ...shares,
      items: [resultId].concat((shares && shares.items) || []),
    }))
  },

  [PLAYLIST_SHARES_LOADING](state, { id }) {
    return updateShares(state, id, shares => ({
      ...shares,
      error: null,
      errorMessage: null,
      loading: true,
      loaded: false,
    }))
  },

  [PLAYLIST_SHARES_LOADING_SUCCESS](state, { id, responseShares }) {
    return updateShares(state, id, shares => ({
      ...shares,
      items: responseShares.map(item => item.id),
      error: null,
      errorMessage: null,
      loading: false,
      loaded: true,
    }))
  },

  [PLAYLIST_SHARES_LOADING_ERROR](state, { id, error, errorMessage }) {
    return updateShares(state, id, shares => ({
      ...shares,
      error,
      errorMessage,
      loading: false,
      loaded: false,
    }))
  },

  [PLAYLIST_ACTION_STATUS_SET](state, { id, status }) {
    if (!state[id]) return state
    return {
      ...state,
      [id]: {
        ...state[id]!,
        actionStatus: {
          // @ts-expect-error - actionStatus is not defined in the type
          ...state[id]!.actionStatus,
          ...status,
        },
      },
    }
  },

  [PLAYLIST_LOAD_ZIP_URLS_SUCCESS](state, { playlist, urls }) {
    const id = playlist.playlist_id
    if (!state[id]) return state
    return { ...state, [id]: { ...state[id]!, zip_urls: urls } }
  },

  [PLAYLIST_LOAD_PARAMS](
    state,
    {
      playlist,
    }: {
      playlist: Playlist
    },
  ) {
    const id = playlist.playlist_id
    if (!state[id]) return state
    return {
      ...state,
      [id]: {
        ...state[id]!,
        has_any_territoryless_track: playlist.has_any_territoryless_track,
      },
    }
  },

  [PLAYLIST_LOAD_SUCCESS](state, { playlistId, response }) {
    // TODO: Proper normalization
    if (!state[playlistId]) return state
    return {
      ...state,
      [playlistId]: {
        ...state[playlistId]!,
        album_image_url: response.album_image_url,
        color: response.color,
        image_url: response.image_url,
        release_date: response.release_date,
      },
    }
  },

  [PLAYLIST_UPDATE_SUCCESS](state, { playlistId, response }) {
    // TODO: Proper normalization
    if (!state[playlistId]) return state
    return {
      ...state,
      [playlistId]: {
        ...state[playlistId]!,
        album_image_url: response.album_image_url,
        color: response.color,
        image_url: response.image_url,
        release_date: response.release_date,
      },
    }
  },

  [PLAYLISTS_UPDATE](state, { playlistIds }) {
    return applyChangeSet(state, playlistIds, { loading: true })
  },

  [PLAYLISTS_UPDATE_SUCCESS](state, { playlistIds, data }) {
    return applyChangeSet(state, playlistIds, {
      ...data,
      loading: false,
    })
  },

  [PLAYLISTS_UPDATE_FAIL](state, { playlistIds }) {
    return applyChangeSet(state, playlistIds, { loading: false })
  },
})
